import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { DateTime } from "luxon"

// Context
import { UserContext } from "../../../context/userContext"

//PrimeReact
import { Dropdown } from "primereact/dropdown"
import { Chart } from "primereact/chart"
import { Button } from "primereact/button"
import { OverlayPanel } from "primereact/overlaypanel"
import { Badge } from "primereact/badge"
import { Tooltip } from "primereact/tooltip"

//Helpers
import { LogError, GetLogLevelKey } from "../../../helpers/logger"
import { assessmentsApiUrl } from "../../../config/microservicesUrls"
import CustomTable from "../../../components/CustomTable/CustomTable"

//Styles
import assessStyles from "../../../styles/assessments/Assessment.module.css"
import myStyles from "../../../styles/assessments/ParticipantAssessments.module.css"

//SOAR Components
import { LoaderMedium, LoaderSmall } from "../../../components/Loaders"

//Assessments components
import {
  assessmentsApiParticipantOverviewPost,
  assessmentsApiParticipantOverviewSummaryPost,
} from "../assessmentsApi"

//SignalR
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr"
import { classNames } from "primereact/utils"
import { formatCompletedDateToLocaleString } from "../assessmentHelpers"

export default function ParticipantAssessmentsOverview({
  participantServiceEpisode,
  selectedOrganizationId,
  setIsChildLoading,
}) {
  const userCtx = useContext(UserContext)
  const overlayPanelRef = useRef(undefined)
  const [overView, setOverView] = useState(undefined)
  const [pageSetup, setPageSetup] = useState({
    rows: 10,
    first: 0,
    page: 0,
    sortField: "completedDate",
    sortOrder: "z-a",
  })

  const [graphData, setGraphData] = useState(undefined)
  const [graphPeriod, setGraphPeriod] = useState("year")
  const [graphType, setGraphType] = useState("line")
  const [isLoadingOverview, setIsLoadingOverview] = useState(true)
  const [isLoadingGraph, setIsLoadingGraph] = useState(true)
  const [filterOptions, setFilterOptions] = useState([
    { label: "View all assessments", value: "0" },
  ])
  const [filterValue, setFilterValue] = useState("0")
  const [assessmentHubConnection, setAssessmentHubConnection] = useState(null)
  const [hasUpdate, setHasUpdate] = useState(false)

  function getCurrentOrg() {
    return userCtx?.participant_organizations?.find(
      (o) => o.organization_id === selectedOrganizationId,
    )
  }

  // SignalR
  const setupSignalRConnection = async () => {
    try {
      // Setup Connection
      const connection = new HubConnectionBuilder()
        .withUrl(`${assessmentsApiUrl()}/hubs/clientComms`)
        .withAutomaticReconnect()
        .configureLogging(
          GetLogLevelKey() === "debug" ? LogLevel.Debug : LogLevel.Warning,
        )
        .build()

      // Register client-side method
      connection.on(
        "SendAssessmentConfirmationNotification",
        (participantId, organizationId) => {
          if (
            participantId === userCtx.participant_id &&
            organizationId === selectedOrganizationId &&
            !hasUpdate
          ) {
            setHasUpdate(true)
            userCtx.setNotificationInformation(
              "There are newly completed assessments for this participant, please refresh the overview dataset to view updated values",
            )
          }
        },
      )

      // Start the connection
      await connection.start()
      console.log("Connected to Assessment Hub")
      setAssessmentHubConnection(connection) // Save connection to state
    } catch (error) {
      LogError("Unable to connect to the Assessment Hub", error)
      setAssessmentHubConnection(undefined)
    }
  }

  useEffect(() => {
    setupSignalRConnection()
    return () => {
      if (assessmentHubConnection) {
        assessmentHubConnection.stop()
        console.log("Disconnected from Assessment Hub")
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadOverviewData = useCallback(async () => {
    try {
      if (!participantServiceEpisode?.isLoading) {
        setIsLoadingOverview(true)
        setIsChildLoading(true)

        //Default to 1 year ago
        let startDate = DateTime.now().plus({ days: -365 })

        if (
          participantServiceEpisode?.serviceEpisode &&
          participantServiceEpisode?.serviceEpisode.length > 0
        ) {
          var firstEntry = participantServiceEpisode?.serviceEpisode.find(
            (entry) =>
              entry.episode_intake_date !== null &&
              entry.service_episodes_id.endsWith("100"), //Intake entries end with 100, discharge with 200
          )
          if (firstEntry) {
            startDate = DateTime.fromFormat(
              firstEntry.episode_intake_date,
              "yyyy-MM-dd",
            )
          }
        }

        let payload = {
          participantId: userCtx.participant_id,
          organizationId: selectedOrganizationId,
          enterpriseId: getCurrentOrg()?.enterprise_id,
          startDate: startDate.toISODate(),
          pageSize: 3,
          pageNumber: pageSetup.page + 1,
        }

        if (filterValue !== "0") {
          payload = { ...payload, assessmentId: filterValue }
        }

        const result = await assessmentsApiParticipantOverviewPost(
          userCtx.tokens.getAssessmentsToken(),
          payload,
        )

        result.assessmentTypes.forEach((type) => {
          type.lastAssessmentDateValue = type.lastAssessment
            ? DateTime.fromISO(type.lastAssessment, {
                zone: "utc",
              }).toJSDate()
            : undefined
        })

        setOverView(result)

        const newFilterOptions = [
          { label: "View all assessments", value: "0" },
          ...result.assessmentTypes?.map((type) => {
            return { label: type.assessmentName, value: type.assessmentId }
          }),
        ]

        setFilterOptions(newFilterOptions)
        setPageSetup({ ...pageSetup, first: 0, page: 0 })
        setIsLoadingOverview(false)
        setIsChildLoading(false)
      }
    } catch (error) {
      LogError("Unable to load participant overview", error)
      userCtx.setNotificationError("Unable to load participant overview")
      setOverView(undefined)
      setIsLoadingOverview(false)
      setIsChildLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterValue,
    userCtx.participant_id,
    selectedOrganizationId,
    participantServiceEpisode,
  ])

  const loadOverviewGraphData = useCallback(async () => {
    try {
      if (filterValue !== "0") {
        setIsLoadingGraph(true)
        setIsChildLoading(true)

        let payload = {
          participantId: userCtx.participant_id,
          organizationId: selectedOrganizationId,
          assessmentId: filterValue,
          periodType: graphPeriod,
        }

        const result = await assessmentsApiParticipantOverviewSummaryPost(
          userCtx.tokens.getAssessmentsToken(),
          payload,
        )

        //Summarize graph data
        let dataSet = {}

        result?.data.forEach((data) => {
          let dateKey = DateTime.fromISO(data.date)
            .toLocal()
            .toFormat("yyyyMMdd")

          if (Object.keys(dataSet).includes(dateKey)) {
            dataSet[dateKey].counter = dataSet[dateKey].counter + 1
            dataSet[dateKey].values = [...dataSet[dateKey].values, data.score]
            dataSet[dateKey].label =
              `${dataSet[dateKey].date} [${dataSet[dateKey].values.length}] *`

            let totalScore = dataSet[dateKey].values.reduce((a, b) => a + b, 0)

            dataSet[dateKey].averageValue =
              totalScore / dataSet[dateKey].values.length
          } else {
            let dateString = DateTime.fromISO(data.date)
              .toLocal()
              .toLocaleString()

            let newDataSet = {
              ...dataSet,
            }

            newDataSet[dateKey] = {
              date: dateString,
              label: dateString,
              counter: 1,
              values: [data.score],
              averageValue: data.score,
            }

            dataSet = newDataSet
          }
        })

        setGraphData({
          labels: Object.keys(dataSet).map((dataKey) => dataSet[dataKey].label),
          datasets: [
            {
              label: "Score",
              data: Object.keys(dataSet).map(
                (dataKey) => dataSet[dataKey].averageValue,
              ),
              borderColor: "#8467ad",
              backgroundColor: "#E7E0F1",
              fill: true,
              tension: 0.4,
            },
          ],
        })
        setIsLoadingGraph(false)
        setIsChildLoading(false)
      }
    } catch (error) {
      LogError("Unable to load participant overview summary", error)
      userCtx.setNotificationError(
        "Unable to load participant overview summary",
      )
      setGraphData(undefined)
      setIsLoadingGraph(false)
      setIsChildLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, graphPeriod, userCtx.participant_id, selectedOrganizationId])

  useEffect(() => {
    loadOverviewData()
  }, [loadOverviewData])

  useEffect(() => {
    loadOverviewGraphData()
  }, [loadOverviewGraphData])

  const LastAssessmentDateColumnTemplate = (rowData) => {
    let date = rowData.lastAssessment || rowData.completedDate

    if (!!date) {
      date = formatCompletedDateToLocaleString(date)
    }

    if (!date) {
      return <span>-</span>
    }

    return <>{date}</>
  }

  const OverviewActionColumnTemplate = (rowData) => {
    return (
      <>
        <Button
          type="button"
          onClick={() => {
            setFilterValue(rowData.assessmentId)
          }}
          className={assessStyles.actionButtonPurple}
          tooltip="Show detail for this assessment type"
          tooltipOptions={{ position: "bottom" }}
          style={{
            borderRadius: "28px",
            color: "#873CAA",
            backgroundColor: "#ffff",
            borderColor: "#873CAA",
            padding: "16px 24px",
          }}
        >
          View
        </Button>
      </>
    )
  }

  const SummaryCard = ({
    icon,
    iconClass,
    label,
    value,
    tooltip,
    hasTooltip,
  }) => (
    <div className={myStyles.overviewSummaryLayout}>
      <div className={myStyles.overviewSummaryLayoutLogo}>
        <span
          className={classNames(iconClass, { ttSummary: hasTooltip })}
          style={{ fontSize: "15px" }}
          data-pr-tooltip={tooltip}
          data-pr-position="bottom"
        >
          {icon}
        </span>
      </div>
      <div className="flex flex-column">
        <div
          className={classNames(
            myStyles.overviewSummaryLayoutDescription,
            hasTooltip && "ttSummary",
          )}
        >
          {label}
        </div>
        <div
          className={classNames(
            myStyles.overviewSummaryLayoutCounter,
            hasTooltip && "ttSummary",
          )}
        >
          {isLoadingOverview ? (
            <LoaderSmall
              style={{
                margin: "0px",
                marginTop: "10px",
                height: "15px",
                width: "fit-content",
              }}
            />
          ) : (
            <>{value}</>
          )}
        </div>
      </div>
    </div>
  )

  const OverviewCards = () => {
    return (
      <>
        <Tooltip target=".ttSummary" />

        {/* Always show these */}
        <div
          className={classNames(
            myStyles.OverviewLayoutSummary,
            filterValue !== "0" && myStyles.summaryMarginBottom,
          )}
        >
          <SummaryCard
            icon="assignment_turned_in"
            iconClass="material-icons"
            label="Completed Assessments"
            value={overView?.assessmentsGiven ?? 0}
            tooltip="The total number of assessments completed for this participant in the current service episode"
            hasTooltip={true}
          />

          <SummaryCard
            icon="trending_up"
            iconClass="material-icons"
            label="Monthly Average"
            value={getAverageAssessmentsCompleted()}
            tooltip="The average number of assessments completed for this participant per month since start of current service episode"
            hasTooltip={true}
          />

          <SummaryCard
            icon="calendar_today"
            iconClass="material-icons"
            label="Last assessment"
            value={getLastAssessmentDate()}
            hasTooltip={false}
          />
        </div>

        {/* Conditionally show these based on filterValue */}
        {filterValue !== "0" && (
          <div className={myStyles.OverviewLayoutSummary2}>
            <SummaryCard
              icon="data_thresholding"
              iconClass="material-icons"
              label="Baseline score"
              value={overView?.filteredBaselineScore ?? 0}
              hasTooltip={false}
            />

            <SummaryCard
              icon="low_priority"
              iconClass="material-icons"
              label="Latest score"
              value={overView?.filteredLatestScore ?? 0}
              hasTooltip={false}
            />

            <SummaryCard
              icon="checklist"
              iconClass="material-icons"
              label="Score change"
              value={overView?.filteredScoreChange ?? 0}
              hasTooltip={false}
            />
          </div>
        )}
      </>
    )
  }

  const OverviewTable = () => {
    return (
      <CustomTable
        headers={headers()}
        collection={mapData()}
        styles={{ padding: "16px", borderRadius: "12px", marginTop: "0px" }}
        tableRowStyles={{ backgroundColor: "#FFFFFF" }}
        filters={filters()}
        sorting={sorting()}
      />
    )
  }

  const OverViewGraph = () => {
    let options = {
      maintainAspectRatio: false,
      aspectRatio: 0.6,
    }

    let graphTypeValue = graphType
    if (graphType === "verticalBar") {
      graphTypeValue = "bar"
      options = { ...options, indexAxis: "y" }
    }

    let assessmentProfile = filterOptions.filter(
      (option) => option.value === filterValue,
    )

    return (
      <div className={myStyles.overviewGraphLayout}>
        <div className={myStyles.overviewGraphLayoutHeader}>
          Scores for{" "}
          {assessmentProfile.length > 0
            ? assessmentProfile[0].label
            : "selected assessment"}
        </div>
        <div className={myStyles.overviewGraphLayoutPeriod}>
          <Dropdown
            value={graphPeriod}
            options={[
              { label: "All time", value: "all" },
              { label: "Last year", value: "year" },
              { label: "Last 180 days", value: "180days" },
              { label: "Last 90 days", value: "90days" },
              { label: "Last 30 days", value: "30days" },
              { label: "Last 7 days", value: "7days" },
            ]}
            onChange={(e) => {
              setGraphPeriod(e.value)
            }}
            className={myStyles.overviewGraphDropDown}
          />
        </div>
        <div className={myStyles.overviewGraphLayoutType}>
          <Dropdown
            value={graphType}
            options={[
              { label: "Line", value: "line" },
              { label: "Bar", value: "bar" },
              { label: "Vertical Bar", value: "verticalBar" },
            ]}
            onChange={(e) => {
              setGraphType(e.value)
            }}
            className={myStyles.overviewGraphDropDown}
          />
        </div>
        <div className={myStyles.overviewGraphLayoutGraph}>
          <Chart type={graphTypeValue} data={graphData} options={options} />
          {graphData?.labels?.some((label) => label.includes("*")) ? (
            <span className={myStyles.graphHelper}>
              * Dates with a [number] indicate that there are multiple
              assessments on the day and the score is an average
            </span>
          ) : null}
        </div>
      </div>
    )
  }

  const getAverageAssessmentsCompleted = () => {
    if (
      participantServiceEpisode &&
      participantServiceEpisode?.serviceEpisode &&
      participantServiceEpisode?.serviceEpisode.length > 0
    ) {
      const serviceEpisode = participantServiceEpisode?.serviceEpisode.find(
        (entry) =>
          entry.episode_intake_date !== null &&
          entry.service_episodes_id.endsWith("100"), //Intake entries end with 100, discharge with 200
      )

      if (serviceEpisode) {
        const startDate = DateTime.fromFormat(
          serviceEpisode.episode_intake_date,
          "yyyy-MM-dd",
        )

        const dischargeDate = serviceEpisode.episode_discharge_date
          ? DateTime.fromFormat(
              serviceEpisode.episode_discharge_date,
              "yyyy-MM-dd",
            )
          : DateTime.now()

        if (!startDate.invalid) {
          const difference = dischargeDate.diff(startDate, ["months"])

          return (
            Math.round(
              (overView?.assessmentsGiven / difference.values.months) * 100,
            ) / 100
          )
        }
      }
    }

    return "Unavailable"
  }

  const getLastAssessmentDate = () => {
    return overView?.lastAssessmentDate
      ? formatCompletedDateToLocaleString(overView.lastAssessmentDate)
      : "Unavailable"
  }

  const headers = () => {
    return [
      {
        value: "assessmentName",
        label: "Assessment Name",
        size: 4,
      },
      {
        value: "completedAssessments",
        label: "Completed Assessments",
        size: 4,
      },
      {
        value: "completedDate",
        label: "Date of last administration",
        size: 3,
      },
      {
        value: "view",
        label: "",
        action: true,
        size: 1,
      },
    ]
  }

  const mapOrganization = (organization) => {
    if (!organization) {
      return "Unknown Organization"
    }
    if (organization === false) {
      return "Unknown Organization"
    }
    return organization
  }

  const mapData = () => {
    return overView?.assessmentTypes?.map((d) => {
      return {
        assessmentName: mapOrganization(d.assessmentName),
        completedAssessments: d?.count ? d.count : "0",
        completedDate: <LastAssessmentDateColumnTemplate {...d} />,
        view: <OverviewActionColumnTemplate {...d} />,
        _lastAssessment: d.lastAssessment, // Used for sorting
        _count: d.count.toString(), // Used for sorting
      }
    })
  }

  const filters = () => {
    return [
      {
        value: "search",
        label: "",
        placeholder: "Keyword Search",
        showClear: true,
        type: "search",
      },
    ]
  }

  const sorting = () => {
    return [
      {
        value: "sortBy",
        label: "Sort by",
        options: [
          { name: "Assessment Name", code: "assessmentName" },
          { name: "Completed Assessments", code: "_count" },
          { name: "Date of last administration", code: "_lastAssessment" },
        ],
        default: "assessmentName",
        defaultOrder: true,
        placeholder: "Select option",
        showClear: true,
        type: "multiselect",
      },
    ]
  }

  return (
    <>
      <OverlayPanel
        ref={overlayPanelRef}
        className={assessStyles.InfoOverlayPanel}
      >
        <p className={assessStyles.InfoOverlayText}>
          To view time series outcomes for a specific assessment, <br />
          please select the assessment from the filter list.
        </p>
      </OverlayPanel>

      <div className={myStyles.OverviewLayout}>
        <div
          className={`${myStyles.OverviewLayoutHeader} ${assessStyles.sectionMarginBottom}`}
        >
          <h2 className="mb-0">Metrics and Reports</h2>
        </div>

        <div
          className={`${myStyles.OverviewLayoutAction} ${assessStyles.sectionMarginBottom}`}
        >
          <div className={myStyles.OverviewLayoutActionPartial}>
            <div
              className={`${assessStyles.InfoOverlayIconContainer} ${myStyles.InfoOverlayIconContainer}`}
              onMouseEnter={(e) => {
                overlayPanelRef.current.show(e)
              }}
              onMouseLeave={(e) => {
                overlayPanelRef.current.hide(e)
              }}
              onClick={(e) => {}}
            >
              <span
                className="pi pi-info"
                style={{
                  backgroundColor: "transparent",
                  padding: "4px",
                  display: "inline-flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "6px",
                  border: "1px solid #873CAA",
                  fontSize: "10px",
                }}
              />
            </div>

            <Dropdown
              value={filterValue}
              options={filterOptions}
              optionLabel="label"
              optionValue="value"
              className={myStyles.viewSelect}
              onChange={(e) => {
                setFilterValue(e.value)
              }}
            />
          </div>

          <div className={myStyles.refreshContainer}>
            <Button
              className={`${assessStyles.button} ${myStyles.refreshButton}`}
              style={{ marginTop: "0px" }}
              icon="pi pi-sync"
              onClick={() => {
                loadOverviewData()

                if (hasUpdate) {
                  setHasUpdate(false)
                }
              }}
              tooltip={`${
                hasUpdate
                  ? "Refresh data, newly confirmed assessments available"
                  : "Refresh data"
              }`}
              tooltipOptions={{ position: "bottom" }}
            />
            {hasUpdate ? (
              <Badge
                severity="warning"
                value={"!"}
                className={myStyles.refreshBadge}
              />
            ) : null}
          </div>
        </div>

        {/* Overview cards, table and graph */}
        {participantServiceEpisode?.isLoading === false &&
          ((filterValue === "0" && !isLoadingOverview) ||
            (filterValue !== "0" && !isLoadingGraph)) && (
            <>
              <OverviewCards />

              <div className={myStyles.OverviewLayoutGraph}>
                {filterValue === "0" ? <OverviewTable /> : <OverViewGraph />}
              </div>
            </>
          )}
      </div>

      {(participantServiceEpisode?.isLoading === true ||
        (filterValue === "0" && isLoadingOverview) ||
        (filterValue !== "0" && isLoadingGraph)) && (
        <div style={{ width: "100%" }}>
          <LoaderMedium />
        </div>
      )}
    </>
  )
}
